/**
 * PATH: apps/quote/src/pages/pricelist-page/HistoricalTab.tsx
 * PURPOSE: Historical pricing analysis tab with summary stats and data table
 */

import { Database, Loader2, FileSpreadsheet } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

interface HistoricalTabProps {
  historicalData: any;
  historicalLoading: boolean;
  generatePricebookMutation: any;
  onOpenWizard: () => void;
}

export default function HistoricalTab({
  historicalData,
  historicalLoading,
  generatePricebookMutation,
  onOpenWizard,
}: HistoricalTabProps) {
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Quotes Analyzed', value: historicalData?.quotes_analyzed || 0 },
          { label: 'Unique Items', value: historicalData?.unique_items || 0 },
          { label: 'Avg Rate', value: historicalData?.summary?.avg_rate ? formatCurrency(historicalData.summary.avg_rate) : '—' },
          { label: 'Avg Margin', value: historicalData?.summary?.avg_margin ? `${historicalData.summary.avg_margin.toFixed(1)}%` : '—' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={onOpenWizard}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <FileSpreadsheet className="h-4 w-4" />
          Build from job quotes…
        </button>
        <button
          onClick={() => generatePricebookMutation.mutate()}
          disabled={generatePricebookMutation.isPending || !historicalData?.items?.length}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          <Database className="h-4 w-4" />
          {generatePricebookMutation.isPending ? 'Creating...' : 'Create Pricebook from History'}
        </button>
        <span className="text-sm text-gray-500">
          Pick jobs and harvest their quote line-items into the current pricelist, or generate a new pricebook from all history.
        </span>
      </div>

      {/* Historical Items Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {historicalLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : !historicalData?.items?.length ? (
          <div className="p-8 text-center text-gray-500">No historical pricing data found. Create some quotes first!</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Description</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Uses</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Unit</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Avg Rate</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Min</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Max</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {historicalData.items.slice(0, 50).map((item: any, idx: number) => (
                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 text-gray-900 dark:text-white">{item.description}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">{item.occurrences}</span>
                  </td>
                  <td className="px-6 py-4 text-center text-gray-600 dark:text-gray-400">{item.unit}</td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(item.rate_avg)}</td>
                  <td className="px-6 py-4 text-right text-gray-500 text-sm">{formatCurrency(item.rate_min)}</td>
                  <td className="px-6 py-4 text-right text-gray-500 text-sm">{formatCurrency(item.rate_max)}</td>
                  <td className="px-6 py-4 text-right text-green-600">{item.margin_avg?.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
